import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Badge, Btn, Card, ConfirmModal, ErrorState, Input, PageHeader, Toggle,
} from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../constants';

// Admin "Data Sync & Compute" tab.
//
// Sport-score caching was retired in favor of live Cerebro scoring
// (sport_score_engine.py). What remains is four bulk-compute pipelines,
// all wired through the same UI shape via the ComputeCard component:
//
//   id               backend module           → writes to athlete doc
//   ──────────────   ──────────────────────   ─────────────────────────────────
//   visibility       social_score/views.py    social_score
//   irl              irl_score_views.py       statistics.breakdown.IRL Impact
//   cerebroLinks     cerebro_records.py       cerebroAliasIds (the AUTO tier)
//   stats            cerebro_enrich.py        statistics.cerebro (the stats refresh)
//   cachescore       cachescore_views.py      statistics.cacheScore
//   nilValuation     nil_value_views.py       statistics.nilValuation + nilRates
//   playerBio        bio_views.py             bio
//   profileInsights  profile_insights.py      statistics.summaryOf*Analytics
//
// Player Bio and Profile Insights make paid AI calls per athlete.
//
// An auto-sync schedule layer (GET/PUT /compute-schedule/) lets admins
// pick a global default interval plus per-pipeline overrides; a Heroku
// Scheduler job calling `python manage.py tick_compute_scheduler` fires
// any pipeline whose interval has elapsed since its last finished run.
// "Last run" timestamps come from the same /compute-schedule/ endpoint.

// Keep these IDs in sync with backend/users/compute_schedule.py:PIPELINES.
const PIPELINE_VISIBILITY = 'visibility';
const PIPELINE_IRL = 'irl';
const PIPELINE_CACHESCORE = 'cachescore';
const PIPELINE_NIL = 'nilValuation';
const PIPELINE_BIO = 'playerBio';
const PIPELINE_INSIGHTS = 'profileInsights';
const PIPELINE_STATS = 'stats';
const PIPELINE_LINKS = 'cerebroLinks';

const formatRelative = (iso) => {
  if (!iso) return 'Never';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  if (Number.isNaN(diffMs)) return 'Unknown';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const ComputeCard = ({
  pipelineId,            // matches backend PIPELINES — used for schedule key
  title,
  description,
  computingLabel,
  endpoints,             // { count, compute, progress }
  pollIntervalMs = 3000,
  computingTitle,
  confirmSubject,
  // From parent: schedule context so the card can show last-run +
  // per-pipeline override input.
  scheduleConfig,        // {interval_days, pipelines: {<id>: {enabled, interval_days}}}
  lastRuns,              // {<id>: {finished_at, success, failed}}
  onOverrideChange,      // (pipelineId, val|null) => void
  onEnabledChange,       // (pipelineId, bool) => void
  onRefreshSchedule,     // () => Promise — re-fetches schedule + last_runs
  actionLabel,           // (count) => button text; default "Compute N Scores"
  doneNoun = 'scored',   // the toast's word for a success ("12 scored")
}) => {
  const token = useSelector((s) => s.auth?.token);
  const [computing, setComputing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef(null);

  const [eligibleCount, setEligibleCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}${endpoints.count}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setEligibleCount(typeof data.count === 'number' ? data.count : null);
    } catch {
      // Non-fatal — UI just shows a generic label without the number.
    } finally {
      setLoadingCount(false);
    }
  }, [token, endpoints.count, authHeaders]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const fetchProgress = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}${endpoints.progress}`, { headers: authHeaders() });
    if (!res.ok) throw new Error('progress fetch failed');
    return res.json();
  }, [endpoints.progress, authHeaders]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const p = await fetchProgress();
        setProgress(p);
        if (!p.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setComputing(false);
          if (p.error) {
            // Hard failure (e.g. expired API token) — surface it loudly
            // instead of a misleading "0 scored" success toast.
            toast.error(`${title}: ${p.error}`);
          } else {
            const skippedTxt = p.skipped ? `, ${p.skipped} kept (no new data)` : '';
            toast.success(`${title} complete: ${p.success} ${doneNoun}, ${p.failed} failed${skippedTxt}`);
          }
          fetchCount();
          if (onRefreshSchedule) onRefreshSchedule();
        }
      } catch {
        // ignore transient polling errors
      }
    }, pollIntervalMs);
  }, [fetchProgress, fetchCount, pollIntervalMs, title, onRefreshSchedule, doneNoun]);

  useEffect(() => {
    let cancelled = false;
    const checkRunning = async () => {
      try {
        const p = await fetchProgress();
        if (cancelled) return;
        if (p.running) {
          setComputing(true);
          setProgress(p);
          startPolling();
        }
      } catch { /* ignore */ }
    };
    checkRunning();
    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [fetchProgress, startPolling]);

  // Runs once the admin confirms in the ConfirmModal.
  const handleClick = async () => {
    setConfirmOpen(false);
    setComputing(true);
    setProgress({ running: true, total: 0, completed: 0, success: 0, failed: 0, skipped: 0, error: null, current_athlete: 'Starting...' });
    try {
      const res = await fetch(`${API_BASE_URL}${endpoints.compute}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Compute request failed');
      toast.success(`Queued ${data.queued ?? 0} athletes. Computing in background...`);
      startPolling();
    } catch (err) {
      toast.error(`Failed to start: ${err.message}`);
      setComputing(false);
      setProgress(null);
    }
  };

  const countLabel = eligibleCount !== null && !loadingCount ? eligibleCount : null;
  const buttonLabel = computing
    ? computingLabel
    : actionLabel
    ? actionLabel(countLabel)
    : countLabel !== null
    ? `Compute ${countLabel.toLocaleString()} Score${countLabel === 1 ? '' : 's'}`
    : 'Compute All Scores';

  // Schedule context for the meta row. Each pipeline owns its own
  // auto-sync toggle + interval; the parent's globalInterval is the
  // fallback when a pipeline doesn't set its own.
  const lastRun = (lastRuns || {})[pipelineId] || {};
  const pipelineCfg = (scheduleConfig?.pipelines || {})[pipelineId] || {};
  const overrideVal = pipelineCfg.interval_days;          // null = use global
  const globalInterval = scheduleConfig?.interval_days || 7;
  const effectiveInterval = overrideVal != null ? overrideVal : globalInterval;
  const autoSyncOn = !!pipelineCfg.enabled;

  const busy = computing || eligibleCount === 0;

  return (
    <Card padding="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h4 className="flex flex-wrap items-center gap-2 bm-label-l text-bm-ink">
            {title}
            {!loadingCount && eligibleCount !== null && (
              <Badge tone="soft">{eligibleCount.toLocaleString()} eligible</Badge>
            )}
          </h4>
          <p className="mt-1 bm-body-s text-bm-muted">
            {description}
            {!loadingCount && eligibleCount !== null && (
              <> Will compute for{' '}
                <span className="bm-label-m text-bm-ink">
                  {eligibleCount.toLocaleString()} athlete{eligibleCount === 1 ? '' : 's'}
                </span>
                .
              </>
            )}
          </p>
        </div>
        <Btn
          variant="secondary"
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          className="ml-auto shrink-0 self-center"
        >
          {computing ? (
            <span className="mr-1.5 h-3 w-3 animate-spin rounded-full border-2 border-bm-border border-t-bm-orange" />
          ) : (
            <Icon name="activity" size={14} className="mr-1.5" />
          )}
          {buttonLabel}
        </Btn>
      </div>

      {/* Schedule meta row: auto-sync toggle for THIS pipeline, its
          interval (override the global default) and the last run summary. */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-bm-border pt-3">
        <div className="bm-body-s text-bm-muted">
          <span className="bm-label-m text-bm-ink">Last run:</span>{' '}
          {formatRelative(lastRun.finished_at)}
          {lastRun.finished_at && (
            <span className="ml-2 text-bm-tertiary">
              · {(lastRun.success ?? 0).toLocaleString()} ok
              {lastRun.failed ? ` · ${lastRun.failed.toLocaleString()} failed` : ''}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 bm-body-s text-bm-muted">
          <span className="inline-flex items-center gap-2">
            <span className="bm-label-m text-bm-ink">Auto-sync</span>
            <Toggle
              on={autoSyncOn}
              onChange={(val) => onEnabledChange(pipelineId, val)}
              label={`Auto-sync ${title}`}
            />
          </span>
          <div className={`flex items-center gap-2 ${autoSyncOn ? '' : 'opacity-50'}`}>
            <span className="bm-label-m text-bm-ink">every</span>
            <Input
              type="number"
              min={1}
              max={365}
              value={overrideVal ?? ''}
              placeholder={`${globalInterval}`}
              disabled={!autoSyncOn}
              aria-label={`${title} interval in days`}
              onChange={(e) => {
                const raw = e.target.value;
                const next = raw === '' ? null : Math.max(1, Math.min(365, parseInt(raw, 10) || 0));
                onOverrideChange(pipelineId, Number.isNaN(next) ? null : next);
              }}
              className="!h-8 !w-16 !px-2 text-center disabled:bg-bm-subtle"
            />
            <span>day{effectiveInterval === 1 ? '' : 's'}</span>
            <span className="text-bm-tertiary">
              {overrideVal == null ? '(using global)' : '(override)'}
            </span>
          </div>
        </div>
      </div>

      {computing && progress && (
        <div className="mt-4 w-full rounded-[8px] bg-bm-subtle p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="bm-label-l text-bm-ink">
              {computingTitle}
            </span>
            <span className="bm-body-s text-bm-muted">
              {progress.completed} / {progress.total}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-bm-border">
            <div
              className="h-full rounded-full bg-bm-orange transition-all duration-500"
              style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="max-w-[60%] truncate bm-body-s text-bm-tertiary">
              {progress.current_athlete ? `Processing: ${progress.current_athlete}` : 'Starting...'}
            </span>
            <span className="bm-body-s text-bm-tertiary">
              <span className="bm-label-m text-bm-positive">{progress.success}</span> scored
              {progress.failed > 0 && (
                <> &middot; <span className="bm-label-m text-bm-critical">{progress.failed}</span> failed</>
              )}
              {progress.skipped > 0 && (
                <> &middot; <span className="bm-label-m text-bm-muted">{progress.skipped}</span> kept</>
              )}
            </span>
          </div>
          {progress.error && (
            <div className="mt-2">
              <ErrorState>{progress.error}</ErrorState>
            </div>
          )}
        </div>
      )}

      {/* A compute run is a routine background job, not a destructive one,
          so this is a plain yes or no with no acknowledgment. */}
      {confirmOpen && (
        <ConfirmModal
          title={`Run ${title}?`}
          body={`This will ${confirmSubject(eligibleCount)}. It runs in the background and may take a while.`}
          confirmLabel="Start compute"
          cancelLabel="Cancel"
          requireAck={false}
          onConfirm={handleClick}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Card>
  );
};

const DEFAULT_GLOBAL_INTERVAL = 7;

const DataSyncComputeTab = () => {
  const token = useSelector((s) => s.auth?.token);

  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    interval_days: DEFAULT_GLOBAL_INTERVAL,
    pipelines: {},
  });
  const [lastRuns, setLastRuns] = useState({});
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const fetchSchedule = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/compute-schedule/`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setScheduleConfig(data.config);
        setLastRuns(data.last_runs || {});
        setScheduleDirty(false);
      }
    } catch {
      // Non-fatal — UI shows defaults if the endpoint is unreachable.
    }
  }, [token, authHeaders]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const handleMasterToggle = (val) => {
    setScheduleConfig((prev) => ({ ...prev, enabled: !!val }));
    setScheduleDirty(true);
  };
  const handleGlobalInterval = (raw) => {
    const next = raw === '' ? DEFAULT_GLOBAL_INTERVAL : Math.max(1, Math.min(365, parseInt(raw, 10) || DEFAULT_GLOBAL_INTERVAL));
    setScheduleConfig((prev) => ({ ...prev, interval_days: next }));
    setScheduleDirty(true);
  };
  // Per-pipeline edits preserve the other field (enabled / interval_days).
  const patchPipeline = (pipelineId, patch) => {
    setScheduleConfig((prev) => ({
      ...prev,
      pipelines: {
        ...(prev.pipelines || {}),
        [pipelineId]: {
          ...((prev.pipelines || {})[pipelineId] || {}),
          ...patch,
        },
      },
    }));
    setScheduleDirty(true);
  };
  const handleOverrideChange = (pipelineId, val) => patchPipeline(pipelineId, { interval_days: val });
  const handleEnabledChange = (pipelineId, val) => patchPipeline(pipelineId, { enabled: !!val });
  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await fetch(`${API_BASE_URL}/compute-schedule/`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(scheduleConfig),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'save failed');
      setScheduleConfig(data.config);
      setLastRuns(data.last_runs || {});
      setScheduleDirty(false);
      toast.success('Auto-sync schedule saved.');
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSavingSchedule(false);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-full pb-8">
      <PageHeader eyebrow="Admin Mode" title="Data & Compute" subtitle="Run compute jobs that refresh derived athlete data, and auto-sync them on a schedule." />

      {/* Auto-sync card: global toggle, default interval and save. Per-pipeline
          overrides live on each ComputeCard's meta row below. */}
      <section className="mb-6">
        <h3 className="mb-3 bm-h-s text-bm-ink">Auto-Sync</h3>
        <Card padding="p-5">
          <div className="flex flex-wrap items-center gap-6">
            {/* Master kill switch: when off, every per-pipeline toggle
                below is overridden off and the tick command short-circuits.
                Lets admins pause everything during maintenance without
                losing their per-pipeline preferences. */}
            <span className="inline-flex items-center gap-3">
              <span className="bm-label-l text-bm-ink">Enable auto-sync (master)</span>
              <Toggle
                on={!!scheduleConfig.enabled}
                onChange={handleMasterToggle}
                label="Enable auto-sync (master)"
              />
            </span>

            <div className="flex items-center gap-2">
              <span className="bm-label-l text-bm-ink">Default interval:</span>
              <Input
                type="number"
                min={1}
                max={365}
                value={scheduleConfig.interval_days ?? DEFAULT_GLOBAL_INTERVAL}
                onChange={(e) => handleGlobalInterval(e.target.value)}
                aria-label="Default interval in days"
                className="!w-20 text-center"
              />
              <span className="bm-body-m text-bm-muted">days</span>
            </div>

            <Btn
              variant="secondary"
              onClick={handleSaveSchedule}
              disabled={!scheduleDirty || savingSchedule}
              className="ml-auto"
            >
              {savingSchedule ? 'Saving...' : 'Save schedule'}
            </Btn>
          </div>
          <p className="mt-3 bm-body-s text-bm-muted">
            The master toggle is the kill switch. Turn it off to pause every pipeline at once without losing per-pipeline preferences.
            Each card below has its own auto-sync toggle and can override the default interval.
            A scheduled task running <code className="rounded bg-bm-subtle px-1 text-[11px]">manage.py tick_compute_scheduler</code> every 1 to 6 hours fires the due ones.
          </p>
        </Card>
      </section>

      {/* Stats from Cerebro first (notes 10/19): the AUTO tier links the
          records hard evidence makes certain, the stats refresh reads every
          record known to be an athlete's; both are off until turned on, and
          run before CacheScore so the scores blend fresh stats. */}
      <section className="mb-6">
        <h3 className="mb-3 bm-h-s text-bm-ink">Stats and Records</h3>
        <div className="space-y-4">
          <ComputeCard
            pipelineId={PIPELINE_LINKS}
            title="Automatic Record Links (Cerebro)"
            description="Links the Cerebro records hard evidence makes certain to each live athlete, backed up and logged; anything less stays a suggestion."
            computingLabel="Linking..."
            computingTitle="Linking Cerebro records"
            doneNoun="linked"
            actionLabel={(count) => (count == null ? 'Check all athletes' : `Check ${count.toLocaleString()} athlete${count === 1 ? '' : 's'}`)}
            endpoints={{
              count: '/cerebro-links/eligible-count/',
              compute: '/cerebro-links/compute-all/',
              progress: '/cerebro-links/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'check every live athlete for Cerebro records to link automatically'
              : `check ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'} for Cerebro records to link automatically`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_STATS}
            title="Stats Refresh (Cerebro)"
            description="Refreshes the stats and scores of live athletes whose stats are more than a day old or whose records changed; a failed refresh keeps the saved stats."
            computingLabel="Refreshing..."
            computingTitle="Refreshing stats"
            doneNoun="refreshed"
            actionLabel={(count) => (count == null ? 'Refresh stats' : `Refresh ${count.toLocaleString()} athlete${count === 1 ? '' : 's'}`)}
            endpoints={{
              count: '/stats-refresh/eligible-count/',
              compute: '/stats-refresh/compute-all/',
              progress: '/stats-refresh/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'refresh the stats of every live athlete who is due'
              : `refresh the stats of ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'}`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 bm-h-s text-bm-ink">Scores Compute</h3>
        <div className="space-y-4">
          <ComputeCard
            pipelineId={PIPELINE_VISIBILITY}
            title="Visibility Scores (Bright Data)"
            description="Recomputes social-media-based visibility scores for every athlete with social URLs on their profile."
            computingLabel="Computing..."
            computingTitle="Computing Visibility Scores"
            endpoints={{
              count: '/social-score/eligible-count/',
              compute: '/social-score/compute-all/',
              progress: '/social-score/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'compute visibility scores for ALL athletes with social media URLs'
              : `compute visibility scores for ${count.toLocaleString()} athlete${count === 1 ? '' : 's'} with social media URLs`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_IRL}
            title="Off-Court Impact (IRL)"
            description="Recomputes the off-court impact score (GPA, awards, community service, internships, enrichment) for every live athlete using the current rubric."
            computingLabel="Computing..."
            computingTitle="Computing Off-Court Impact"
            endpoints={{
              count: '/irl-score/eligible-count/',
              compute: '/irl-score/compute-all/',
              progress: '/irl-score/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'recompute off-court impact for ALL live athletes'
              : `recompute off-court impact for ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'}`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_CACHESCORE}
            title="CacheScore (composite)"
            description="Recomputes the composite CacheScore (Performance 80% + Visibility 15% + Off-Court 5%) for every live athlete and persists it to the athlete doc. Run Visibility + Off-Court computes first so the inputs are populated."
            computingLabel="Computing..."
            computingTitle="Computing CacheScore"
            endpoints={{
              count: '/cachescore/eligible-count/',
              compute: '/cachescore/compute-all/',
              progress: '/cachescore/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'recompute CacheScore for ALL live athletes'
              : `recompute CacheScore for ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'}`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_NIL}
            title="NIL Valuation (CacheValue, CACHENILx, NILx Rate Card)"
            description="Recomputes CacheValue, CACHENILx Value, and the NILx Rate Card for every live athlete and persists them to the athlete doc so dashboards and lists show real numbers instead of dashes. Run Visibility + Off-Court computes first so the inputs are populated."
            computingLabel="Computing..."
            computingTitle="Computing NIL Valuation"
            endpoints={{
              count: '/nil-value/eligible-count/',
              compute: '/nil-value/compute-all/',
              progress: '/nil-value/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'recompute NIL valuation + rate card for ALL live athletes'
              : `recompute NIL valuation + rate card for ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'}`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_BIO}
            title="Player Bio (Cache AI)"
            description="Generates the Cache AI player bio for every live athlete whose bio is set to Cache-generated (the default), and writes it to their profile. Athletes who chose to write their own bio are skipped, never overwritten. Bios also regenerate automatically when a cache-mode athlete saves their profile."
            computingLabel="Generating..."
            computingTitle="Generating Player Bios"
            endpoints={{
              count: '/bio/eligible-count/',
              compute: '/bio/compute-all/',
              progress: '/bio/progress/',
            }}
            confirmSubject={(count) => count == null
              ? 'generate Cache bios for ALL eligible live athletes'
              : `generate Cache bios for ${count.toLocaleString()} eligible live athlete${count === 1 ? '' : 's'}`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />

          <ComputeCard
            pipelineId={PIPELINE_INSIGHTS}
            title="Profile Insights (Cache AI)"
            description="Regenerates the Performance and Visibility insights on each live athlete's profile with the focused prompts: Performance covers the player's game only, Visibility covers reach, engagement and off-court activity. Only complete profiles are included, and a section whose inputs have not changed since it was written is skipped. Each regenerated section is a paid AI call. Insights also regenerate when a complete profile is saved."
            computingLabel="Generating..."
            computingTitle="Generating Profile Insights"
            endpoints={{
              count: '/profile-insights/eligible-count/',
              compute: '/profile-insights/compute-all/',
              progress: '/profile-insights/progress/',
            }}
            confirmSubject={(count) => `${count == null
              ? 'regenerate profile insights for ALL live athletes with a complete profile'
              : `regenerate profile insights for ${count.toLocaleString()} live athlete${count === 1 ? '' : 's'} with a complete profile`}. Each changed section is a paid AI call`}
            scheduleConfig={scheduleConfig}
            lastRuns={lastRuns}
            onOverrideChange={handleOverrideChange}
            onEnabledChange={handleEnabledChange}
            onRefreshSchedule={fetchSchedule}
          />
        </div>
      </section>
    </div>
  );
};

export default DataSyncComputeTab;
